import { BLOCK_RULE_CAPS, ResolvedTriggerRule } from './block-rules.js';
import { z } from 'zod';
import { AllowlistRange } from './basics.js';
import { DbTarget } from './db-target.js';
export const Role = z.enum(['base', 'web', 'database']);
/** Knowledge doc section 8.3. Defaults are the doc's defaults. */
export const DiskSettings = z
    .object({
    enabled: z.boolean().default(true),
    interval_seconds: z.number().int().min(30).max(3600).default(120),
    warn_pct: z.number().min(1).max(100).default(80),
    high_pct: z.number().min(1).max(100).default(90),
    critical_pct: z.number().min(1).max(100).default(95),
    cooldown_seconds: z.number().int().min(60).default(1800),
    mounts: z.array(z.string().startsWith('/')).max(100).optional(),
    top_folders_interval_seconds: z.number().int().min(600).default(3600),
})
    .refine((s) => s.warn_pct < s.high_pct && s.high_pct < s.critical_pct, {
    message: 'thresholds must increase: warn_pct < high_pct < critical_pct',
    path: ['warn_pct'],
});
/**
 * The `system` module: processor, load, memory and swap, read from `/proc`. It
 * only measures: charts and trends, no events, so there is nothing to threshold.
 */
export const SystemSettings = z.object({
    enabled: z.boolean().default(true),
    interval_seconds: z.number().int().min(15).max(3600).default(60),
});
/**
 * What the agent does when its hourly look finds a file Bartizo writes changed by
 * somebody else (section 11, "Drift"; P8.3): `put_back`, as it always has, saying
 * so; or `alert`, leaving it and reporting the server as drifted until the file
 * matches the settings again — for an administrator who wants to see a hand edit
 * before anything undoes it. CrowdSec's allowlists are always put back: they keep
 * proxies unbannable (rule 16), which is not something to leave undone.
 */
export const OnDrift = z.enum(['put_back', 'alert']);
export const FimMode = z.enum(['scheduled', 'realtime', 'whodata']);
/**
 * A path Wazuh watches or ignores (section 8.2).
 *
 * It ends up as the text of an element in `ossec.conf`, which Wazuh's root-run
 * daemons read, so it is refused rather than escaped: a comma splits one
 * `<directories>` entry into two paths, and `<`, `>`, `&` or a quote would let a
 * setting write configuration instead of a path — `</directories><localfile>`
 * is a command run as root. Refusing it here puts the error at the API, where a
 * person sees why, rather than in a config test on every server. `*` and `?` are
 * allowed because Wazuh expands them (`/home/*` + `/.ssh`), and `/` alone is
 * not, because it would hash every file on the machine twice a day.
 */
export const FimPathString = z
    .string()
    .min(2)
    .max(1024)
    .startsWith('/')
    .regex(/^[^\p{Cc},<>&"']+$/u, 'must not contain commas, quotes, <, >, & or control characters')
    .refine((path) => path === path.trim(), 'must not start or end with a space')
    .refine((path) => !path
    .split('/')
    .slice(1)
    .some((segment) => segment === '' || segment === '.' || segment === '..'), 'must be a plain absolute path: no empty, "." or ".." segments and no trailing slash');
export const FimPath = z.object({
    path: FimPathString,
    mode: FimMode,
});
/**
 * How often Wazuh scans the scheduled paths, when a layer sets it (P7.3).
 *
 * Wazuh 4.14.8 cannot be asked for a scan on demand, and a restart's first scan
 * reports nothing, so a shorter schedule is how a change under `/usr/bin` is seen
 * sooner (section 12's "rescan files now", as decided). An hour at the shortest:
 * each scan hashes every file under the scheduled paths three ways, and `/var/log`
 * alone can be gigabytes. Unset, Wazuh's own configuration decides — twelve hours
 * where the installer set Wazuh up.
 */
export const FIM_SCAN_MIN_SECONDS = 3600;
export const FIM_SCAN_MAX_SECONDS = 86_400;
export const FimScanIntervalSeconds = z
    .number()
    .int()
    .min(FIM_SCAN_MIN_SECONDS)
    .max(FIM_SCAN_MAX_SECONDS);
/**
 * Section 8.2. `min_level` 5 is Wazuh's own default and drops most noise.
 *
 * `paths` and `ignore` default to empty here because the watched paths come from
 * the roles (`ROLE_DEFAULTS`), which merge beneath every layer: a schema default
 * would be replaced wholesale by the first layer that added one path of its own.
 */
export const WazuhFimSettings = z.object({
    enabled: z.boolean().default(true),
    min_level: z.number().int().min(0).max(15).default(5),
    paths: z.array(FimPath).max(200).default([]),
    ignore: z.array(FimPathString).max(200).default([]),
    scan_interval_seconds: FimScanIntervalSeconds.optional(),
    on_drift: OnDrift.default('put_back'),
});
/**
 * A CrowdSec Hub collection, `author/name` (section 18: "checks CrowdSec collection
 * names against a pattern").
 *
 * Both halves start with a letter or digit. The name becomes one element of
 * `cscli collections install`'s argument array, so it cannot be a second command —
 * but one starting with `-` would be read as a flag, and `..` has no business in
 * something that also names a file under `/etc/crowdsec`.
 */
export const CollectionName = z
    .string()
    .max(128)
    .regex(/^[a-z0-9][a-z0-9_-]*\/[a-z0-9][a-z0-9_-]*(?:\.[a-z0-9_-]+)*$/i, 'must be author/name');
/**
 * Watch or enforce, for one scenario or a whole collection (P11.9.2, decision C3).
 *
 * `watch` is CrowdSec's simulation mode: the scenario still alerts — an event in
 * Bartizo, its decision marked simulated — and bans nobody. It is how a collection
 * just added, or one that bans the wrong people, is tried before it is trusted.
 * `enforce` lifts a wider layer's `watch` for this layer's servers: the list is
 * merged by name and the narrowest layer's entry wins.
 *
 * Bartizo's own `bartizo/*` scenarios are not named here: block rules have their
 * own watch mode (P11.5).
 */
export const CrowdsecMode = z
    .object({
    name: CollectionName.refine((name) => !name.toLowerCase().startsWith('bartizo/'), {
        message: "Bartizo's own scenarios are watched from their block rule",
    }),
    mode: z.enum(['watch', 'enforce']),
})
    .strict();
/**
 * Section 8.1. `poll_seconds` has a floor because issue crowdsecurity/crowdsec#4526
 * shows repeated unfiltered alert queries pinning LAPI CPU at 100%.
 *
 * `collections` are **extra** collections on top of those the installer chose
 * from what runs on the server (P5.1). Listed ones are installed; one the settings
 * stop naming is removed again only if Bartizo installed it (P11.9.2, C4) — never
 * the installer's, never one a person installed by hand.
 */
export const CrowdsecSettings = z.object({
    enabled: z.boolean().default(true),
    poll_seconds: z.number().int().min(15).max(600).default(60),
    collections: z.array(CollectionName).max(100).default([]),
    modes: z.array(CrowdsecMode).max(100).default([]),
});
/** Section 8.4. Off by default: only servers with the database role enable it. */
export const DbSizeSettings = z.object({
    enabled: z.boolean().default(false),
    interval_seconds: z.number().int().min(300).max(86400).default(1800),
    growth_alert_pct_24h: z.number().min(1).max(10000).default(20),
    targets: z.array(DbTarget).max(20).default([]),
});
/**
 * Section 8.5. Off by default, like `db_size`: the database role turns it on.
 *
 * It reads the same targets as `db_size` — see `DbHealthModuleSettings` — so
 * there is nothing here naming a database; only how often to look and what counts
 * as worth telling somebody.
 */
export const DbHealthSettings = z.object({
    enabled: z.boolean().default(false),
    /** Section 8.5: "every 1–5 minutes". */
    interval_seconds: z.number().int().min(60).max(300).default(120),
    /** A query running longer than this counts towards `db_long_queries_count`. */
    long_query_seconds: z.number().int().min(1).max(86_400).default(60),
    /** Connections at or above this share of the maximum raise a `high` event. */
    connections_alert_pct: z.number().min(1).max(100).default(90),
    /** A replica further behind than this raises a `medium` event. */
    replication_lag_alert_seconds: z.number().int().min(1).max(604_800).default(300),
    /**
     * Failed logins within one check that raise a `high` event — when they are also
     * well above what this database usually sees, because some engines count a
     * load balancer's health check as a failed login every few seconds, forever.
     */
    failed_logins_alert_count: z.number().int().min(1).max(1_000_000).default(20),
});
/**
 * Section 8.6's default ports, and MySQL's X Protocol beside its classic one: 8.0,
 * 8.4 and 9.7 all listen on 33060 unless told not to (found in P4.2).
 */
export const DEFAULT_DATABASE_PORTS = {
    3306: 'MySQL or MariaDB',
    33060: 'MySQL X Protocol',
    5432: 'PostgreSQL',
    27017: 'MongoDB',
    6379: 'Redis',
};
/** Section 8.6. Off by default; the database role turns it on. */
export const DbExposureSettings = z.object({
    enabled: z.boolean().default(false),
    /** Section 8.6: "every 15 minutes". */
    interval_seconds: z.number().int().min(300).max(86_400).default(900),
    /**
     * Section 8.6's "or configured ports": database ports besides the defaults,
     * for a server that moved one. A target's own port is watched without being
     * listed here.
     */
    ports: z.array(z.number().int().positive().max(65535)).max(50).default([]),
});
/**
 * Section 8.8's detection (P5.5). Off by default: the web role turns it on.
 *
 * Section 8.8 says detection runs "at install, then every 6 hours", and samples
 * "the first 10 minutes"; both are settings so a quiet server can be given longer
 * to show what is in front of it.
 */
export const WebEnvSettings = z.object({
    enabled: z.boolean().default(false),
    inspect_interval_seconds: z.number().int().min(3600).max(86_400).default(21_600),
    sample_seconds: z.number().int().min(60).max(3600).default(600),
    /** For Bartizo's files in Nginx and Apache (P6.1, P6.6). */
    on_drift: OnDrift.default('put_back'),
    /**
     * P11.9.3 (decision C5): CrowdSec's Nginx bouncer — bans enforced by the web
     * server itself, so they reach visitors behind Cloudflare or a load balancer,
     * where a firewall ban only ever sees the proxy. Off by default; installed the
     * first time it is switched on, only switched off afterwards.
     */
    bouncer: z.boolean().default(false),
    /**
     * P11.9.4 (C7): CrowdSec's AppSec firewall in front of the sites, through the
     * bouncer. `watch` reports what its rules match and blocks nothing; `block`
     * refuses the request (403) and CrowdSec bans an address that keeps trying.
     */
    appsec: z.enum(['off', 'watch', 'block']).default('off'),
    /** P11.9.3 (C6): a challenge (captcha) instead of a ban where asked; needs the captcha keys. */
    challenge: z.boolean().default(false),
});
/**
 * Section 8.7's `web-traffic` module (P6.3): Bartizo's JSON access log, read on the
 * server and sent as a per-minute batch. Off by default: the web role turns it on,
 * and it has nothing to read until an admin confirms the web environment (P6.1),
 * which is what writes the log.
 */
export const WebTrafficSettings = z.object({
    enabled: z.boolean().default(false),
    /** Bot checks in flight at once (P6.4); they are also held to five a second. */
    verify_concurrency: z.number().int().min(1).max(20).default(5),
    /**
     * Section 8.7's traffic spike (P6.5): a minute this many times the last hour's
     * average, and at least a hundred requests, is an event.
     */
    spike_factor: z.number().min(1.5).max(100).default(5),
});
/**
 * What the agent's `db_health` and `db_exposure` modules are actually handed:
 * their own settings, and the one list of targets, which lives under `db_size`
 * because that is where section 8.4 puts it. An administrator names a database
 * once and all three modules read it. `moduleSlices` in `config.ts` does the
 * handing over; no layer can set `targets` on either of these two, because their
 * patches have no such field.
 */
const Targets = z.array(DbTarget).max(20).default([]);
export const DbHealthModuleSettings = DbHealthSettings.extend({ targets: Targets });
export const DbExposureModuleSettings = DbExposureSettings.extend({ targets: Targets });
/**
 * What the agent's `crowdsec` module is handed: its settings, and the document's
 * permanent allowlist, which it keeps CrowdSec's own `bartizo` allowlist equal to
 * (P5.4). The allowlist is top-level in the document because it is a setting of the
 * server rather than of one module; `moduleSlices` hands it over.
 */
export const CrowdsecModuleSettings = CrowdsecSettings.extend({
    allowlist: z.array(AllowlistRange).max(500).default([]),
    /** The CrowdSec triggers (P11.16) the merged block rules resolve to; handed over by `moduleSlices`. */
    triggers: z.array(ResolvedTriggerRule).max(BLOCK_RULE_CAPS.rules).default([]),
});
/**
 * The same settings as a **patch**: every field optional, no defaults, and nothing
 * unknown allowed (knowledge doc section 11's layers).
 *
 * A layer says what it changes and nothing else, so absence has to mean "inherit"
 * rather than "set the default". `.partial()` on the schemas above cannot express
 * that — zod keeps a field's `.default()` underneath the optional wrapper, so an
 * empty patch would parse as every default and a group setting one threshold would
 * silently assert the other seven over whatever global had chosen.
 *
 * `.strict()` rather than stripping unknown keys: a misspelled setting saved from
 * React should be refused at the point somebody can still fix it, not dropped and
 * discovered months later as a threshold that never applied.
 *
 * Cross-field rules are deliberately absent. `warn_pct < high_pct < critical_pct`
 * cannot be checked on a layer that sets one of the three — it is a property of the
 * merged document, and `ConfigDocument` is where it is enforced.
 *
 * `settings.patch.test.ts` pins these against the complete schemas above, because
 * the field lists and their bounds are stated twice and a test is what stops the
 * two copies drifting.
 */
export const DiskSettingsPatch = z
    .object({
    enabled: z.boolean(),
    interval_seconds: z.number().int().min(30).max(3600),
    warn_pct: z.number().min(1).max(100),
    high_pct: z.number().min(1).max(100),
    critical_pct: z.number().min(1).max(100),
    cooldown_seconds: z.number().int().min(60),
    mounts: z.array(z.string().startsWith('/')).max(100),
    top_folders_interval_seconds: z.number().int().min(600),
})
    .partial()
    .strict();
export const SystemSettingsPatch = z
    .object({
    enabled: z.boolean(),
    interval_seconds: z.number().int().min(15).max(3600),
})
    .partial()
    .strict();
export const WazuhFimSettingsPatch = z
    .object({
    enabled: z.boolean(),
    min_level: z.number().int().min(0).max(15),
    paths: z.array(FimPath).max(200),
    ignore: z.array(FimPathString).max(200),
    scan_interval_seconds: FimScanIntervalSeconds,
    on_drift: OnDrift,
})
    .partial()
    .strict();
export const CrowdsecSettingsPatch = z
    .object({
    enabled: z.boolean(),
    poll_seconds: z.number().int().min(15).max(600),
    collections: z.array(CollectionName).max(100),
    modes: z.array(CrowdsecMode).max(100),
})
    .partial()
    .strict();
export const DbSizeSettingsPatch = z
    .object({
    enabled: z.boolean(),
    interval_seconds: z.number().int().min(300).max(86400),
    growth_alert_pct_24h: z.number().min(1).max(10000),
    targets: z.array(DbTarget).max(20),
})
    .partial()
    .strict();
export const DbHealthSettingsPatch = z
    .object({
    enabled: z.boolean(),
    interval_seconds: z.number().int().min(60).max(300),
    long_query_seconds: z.number().int().min(1).max(86_400),
    connections_alert_pct: z.number().min(1).max(100),
    replication_lag_alert_seconds: z.number().int().min(1).max(604_800),
    failed_logins_alert_count: z.number().int().min(1).max(1_000_000),
})
    .partial()
    .strict();
export const DbExposureSettingsPatch = z
    .object({
    enabled: z.boolean(),
    interval_seconds: z.number().int().min(300).max(86_400),
    ports: z.array(z.number().int().positive().max(65535)).max(50),
})
    .partial()
    .strict();
export const WebEnvSettingsPatch = z
    .object({
    enabled: z.boolean(),
    inspect_interval_seconds: z.number().int().min(3600).max(86_400),
    sample_seconds: z.number().int().min(60).max(3600),
    on_drift: OnDrift,
    bouncer: z.boolean(),
    appsec: z.enum(['off', 'watch', 'block']),
    challenge: z.boolean(),
})
    .partial()
    .strict();
export const WebTrafficSettingsPatch = z
    .object({
    enabled: z.boolean(),
    verify_concurrency: z.number().int().min(1).max(20),
    spike_factor: z.number().min(1.5).max(100),
})
    .partial()
    .strict();
//# sourceMappingURL=settings.js.map